import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import {
  LayoutDashboard, Users, Bell, Sparkles, FileText, BarChart3, Settings,
  Sun, Moon, LogOut, ArrowLeft, Droplets, Wand2,
} from "lucide-react";

/** Doctor Shell — left sidebar layout with navigation. */
export default function DoctorShell({ children }) {
  const { user, logout } = useAuth();
  const { language, setLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";

  const NAV = [
    { key: "dashboard", icon: LayoutDashboard, label_bg: "Моето здраве",  label_en: "My Health",   path: "/doctor",   active: true },
    { key: "patients",  icon: Users,            label_bg: "Пациенти",     label_en: "My Patients", path: "/doctor",   active: true, hint_active: true },
    { key: "composer",  icon: Wand2,            label_bg: "Клиничен план", label_en: "Clinical Plan", path: "/doctor/composer", active: true },
    { key: "settings",  icon: Settings,         label_bg: "Настройки",    label_en: "Settings",    path: "/doctor/profile" },
  ];

  const initials = (user?.name || user?.email || "D").slice(0, 2).toUpperCase();

  return (
    <div className={`flex min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-gray-900"}`}
      data-testid="doctor-shell">
      {/* Sidebar — desktop only on >=md (mobile uses DoctorMobileBottomNav) */}
      <aside className={`hidden md:flex w-60 flex-shrink-0 flex-col border-r
        ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"}`}
        data-testid="doctor-sidebar">
        {/* Logo (wordmark вграден в PNG) */}
        <div className="px-5 py-5 border-b flex items-center gap-3
          border-gray-100 dark:border-white/5">
          <img src="/nutramilo-logo.png" alt="Nutramilo" className="h-10 w-auto object-contain" />
          <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
            {bg ? "Доктор" : "Doctor"}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(item => {
            const Icon = item.icon;
            const isActive = item.active;
            return (
              <button
                key={item.key}
                onClick={() => {
                  if (item.disabled) return;
                  if (item.path?.startsWith("#")) {
                    // In-page anchor — let DoctorDashboard listen to hashchange
                    window.location.hash = item.path.slice(1);
                    window.dispatchEvent(new CustomEvent("doctor-nav", { detail: { key: item.key } }));
                  } else {
                    navigate(item.path);
                  }
                }}
                disabled={item.disabled}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                  ${isActive && !item.disabled
                    ? (isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-700")
                    : item.disabled
                      ? (isDark ? "text-gray-600 cursor-not-allowed" : "text-gray-400 cursor-not-allowed")
                      : (isDark ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-600 hover:text-gray-900 hover:bg-gray-50")}`}
                data-testid={`doctor-nav-${item.key}`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1 text-left">{bg ? item.label_bg : item.label_en}</span>
                {item.disabled && <span className="text-[9px] uppercase tracking-wider opacity-70">{bg ? "скоро" : "soon"}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom user */}
        <div className={`border-t px-3 py-3 ${isDark ? "border-white/5" : "border-gray-100"}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold
              bg-gradient-to-br from-violet-500 to-fuchsia-600">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name || user?.email}</div>
              <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                {user?.role === "admin" ? "Admin" : "Clinic Plan"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate("/dashboard")}
              className={`flex-1 inline-flex items-center justify-center gap-1 text-[11px] h-8 rounded-lg
                ${isDark ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`}
              data-testid="back-to-user-view"
            >
              <ArrowLeft className="w-3 h-3" />
              {bg ? "Личен" : "Personal"}
            </button>
            <button
              onClick={toggleTheme}
              className={`w-8 h-8 rounded-lg flex items-center justify-center
                ${isDark ? "text-gray-400 hover:bg-white/5" : "text-gray-600 hover:bg-gray-100"}`}
              aria-label={bg ? "Тема" : "Theme"}
            >
              {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={() => setLanguage(bg ? "en" : "bg")}
              className={`w-8 h-8 rounded-lg text-[10px] font-semibold flex items-center justify-center
                ${isDark ? "text-gray-400 hover:bg-white/5" : "text-gray-600 hover:bg-gray-100"}`}
            >
              {bg ? "EN" : "BG"}
            </button>
            <button
              onClick={logout}
              className={`w-8 h-8 rounded-lg flex items-center justify-center
                ${isDark ? "text-rose-400 hover:bg-white/5" : "text-rose-600 hover:bg-gray-100"}`}
              aria-label="Logout"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">{children}</div>
    </div>
  );
}
